import { ApolloClient, InMemoryCache, gql } from "@apollo/client"
import { getIsLocalHost } from "@pathwright/ui/src/components/utils/env"
import { getGraphQLEndpoint } from "@pathwright/features/src/modules/pathwright/PathwrightClient"
import {
  constructUrl,
  getRelativeUrl,
  getSchoolUrl
} from "@pathwright/features/src/modules/utils/urls"
import { z } from "zod"

// Loosely validating the school query param as a number or string.
const schoolQueryParamSchema = z.union([z.coerce.number().min(1), z.string()])

// Returns the kind and value of the school query param (id or url).
export function getSchoolQueryParam() {
  const { searchParams } = new URL(window.location.href)
  const schoolQueryParam = searchParams.get("school")

  const parsed = schoolQueryParamSchema.safeParse(schoolQueryParam)
  return parsed.success ? parsed.data : null
}

// Query the school by ID from the GraphQL API.
async function getSchoolById() {
  const schoolQueryParam = getSchoolQueryParam()

  if (schoolQueryParam && typeof schoolQueryParam === "number") {
    // Instantiate a basic Apollo Client, enough to query for school.
    const client = new ApolloClient({
      uri: `${getGraphQLEndpoint()}graphql?school_id=${schoolQueryParam}`,
      cache: new InMemoryCache()
    })

    // Query for school by ID.
    const result = await client.query({
      query: gql`
        query SchoolContext {
          school {
            id
            url
          }
        }
      `
    })

    // Return the school URL.
    return result?.data?.school?.url
  }
}

export const transoformLocalConfigURL = function (configUrl) {
  // Ensure http for pathwrightdev urls.
  const protocol = /\.pathwrightdev\.com/.test(configUrl) ? "http" : "https"

  // Ensure protocol for all urls.
  const url = configUrl.replace(/(https*:\/\/|^)/, `${protocol}://`)

  // Return the complete url obj.
  return new URL(url)
}

export const getLocalConfigURL = function () {
  // Fallback to localStorage.
  const configUrl =
    getSchoolQueryParam() || localStorage.getItem("school_config_url")

  if (typeof configUrl !== "string") {
    confirm(
      `Could not load Pathwright client. Set the school URL in the querystring: ${window.location.origin}?school=connect.pathwrightstaging.com`
    )
  }

  return transoformLocalConfigURL(configUrl)
}

// Gets the school config URL, querying by school ID if needed.
// When in local host, you can scope to a school (and possibly specific route)
// using these methods:
// 1. Querystring (ID): ?school=1
// 2. Querystring (URL): ?school=connect.pathwrightstaging.com
// 3. Querystring (URL + path): ?school=connect.pathwrightstaging.com/library/
// 4. Local Storage: school_config_url = connect.pathwrightstaging.com
export const getSchoolConfigURL = async function () {
  if (getIsLocalHost()) {
    return (await getSchoolById()) ?? getLocalConfigURL().origin
  } else {
    return getSchoolUrl()
  }
}

export const createSchoolUrl = getSchoolUrl

// optionally has initial forwardslash
const PRIVATE_API_RE = /(^(?!\/)|\/)api\/private\//
const LEGACY_API_RE = /(^(?!\/)|\/)api\/v1\//

export const getAPIUrl = function (relativeURL) {
  if (!PRIVATE_API_RE.test(relativeURL) && !LEGACY_API_RE.test(relativeURL)) {
    relativeURL = constructUrl("/api/private/", relativeURL)
  }
  return createSchoolUrl(getRelativeUrl(relativeURL))
}

export const encodeURLParams = function (obj) {
  let params = []
  for (let k in obj) {
    const v = obj[k]
    params.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
  }
  return (params = params.join("&"))
}

export const hasURLParameter = (param) => {
  const params = new URLSearchParams(window.location.search)
  return params.has(param)
}

export const getURLParameter = (param) => {
  const params = new URLSearchParams(window.location.search)
  return params.get(param)
}
